import { useState, useEffect, useRef, useCallback } from "react";

/**
 * useCameraManager — Manages camera MediaStreams for the Régie.
 * Handles device enumeration, stream lifecycle, audio toggle,
 * and BroadcastChannel frame relay to ProgramOutput.
 */
export default function useCameraManager(eventId) {
  // sourceId -> { stream, deviceId, audioEnabled, label }
  const [cameraStreams, setCameraStreams] = useState({});
  const [devices, setDevices] = useState([]);
  const [permissionState, setPermissionState] = useState("prompt"); // prompt | granted | denied

  // BroadcastChannel for program camera relay
  const bcRef = useRef(null);
  const relayRef = useRef({ running: false, sourceId: null });
  const canvasRef = useRef(null);
  const videoRelayRef = useRef(null);

  // Enumerate available video devices
  const enumerateDevices = useCallback(async () => {
    try {
      // Request permission first (needed to get device labels)
      const tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      tempStream.getTracks().forEach(t => t.stop());
      setPermissionState("granted");

      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices.filter(d => d.kind === "videoinput");
      setDevices(videoDevices);
      return { ok: true, devices: videoDevices };
    } catch (err) {
      const errName = err.name || "";
      if (errName === "NotAllowedError" || errName === "PermissionDeniedError") {
        setPermissionState("denied");
        return { ok: false, error: "permission_denied", message: "Permission camera refusee par l'utilisateur." };
      }
      if (errName === "NotFoundError") {
        return { ok: false, error: "not_found", message: "Aucune camera detectee sur cet appareil." };
      }
      if (errName === "NotReadableError" || errName === "AbortError") {
        return { ok: false, error: "busy", message: "La camera est utilisee par une autre application." };
      }
      return { ok: false, error: "unknown", message: `Erreur: ${err.message}` };
    }
  }, []);

  // Start capturing a specific camera device
  const startCamera = useCallback(async (sourceId, deviceId, audioEnabled = false) => {
    // Stop any existing stream for this source
    if (cameraStreams[sourceId]?.stream) {
      cameraStreams[sourceId].stream.getTracks().forEach(t => t.stop());
    }

    try {
      const constraints = {
        video: {
          deviceId: { exact: deviceId },
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: audioEnabled,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      setCameraStreams(prev => ({
        ...prev,
        [sourceId]: { stream, deviceId, audioEnabled, active: true },
      }));

      return { ok: true, stream };
    } catch (err) {
      const errName = err.name || "";
      if (errName === "NotReadableError" || errName === "AbortError") {
        return { ok: false, error: "busy", message: "Peripherique occupe ou deconnecte." };
      }
      if (errName === "OverconstrainedError") {
        return { ok: false, error: "overconstrained", message: "Parametres video non supportes par ce peripherique." };
      }
      return { ok: false, error: "unknown", message: `Erreur capture: ${err.message}` };
    }
  }, [cameraStreams]);

  // Stop a camera stream
  const stopCamera = useCallback((sourceId) => {
    setCameraStreams(prev => {
      const entry = prev[sourceId];
      if (entry?.stream) {
        entry.stream.getTracks().forEach(t => t.stop());
      }
      const next = { ...prev };
      delete next[sourceId];
      return next;
    });
    // Stop relay if this camera was being relayed
    if (relayRef.current.sourceId === sourceId) {
      stopRelay();
    }
  }, []);

  // Toggle audio on/off for a camera
  const toggleAudio = useCallback((sourceId) => {
    setCameraStreams(prev => {
      const entry = prev[sourceId];
      if (!entry?.stream) return prev;
      const audioTracks = entry.stream.getAudioTracks();
      const newEnabled = !entry.audioEnabled;
      audioTracks.forEach(t => { t.enabled = newEnabled; });
      return { ...prev, [sourceId]: { ...entry, audioEnabled: newEnabled } };
    });
  }, []);

  // Start BroadcastChannel frame relay for a camera source going to PROGRAM
  const startRelay = useCallback((sourceId) => {
    const entry = cameraStreams[sourceId];
    if (!entry?.stream) return;

    // Stop any existing relay
    stopRelay();

    // Create BroadcastChannel
    const bc = new BroadcastChannel(`glotech-program-camera-${eventId}`);
    bcRef.current = bc;

    // Create hidden video and canvas for frame capture
    const video = document.createElement("video");
    video.srcObject = entry.stream;
    video.muted = true;
    video.playsInline = true;
    video.play();
    videoRelayRef.current = video;

    const canvas = document.createElement("canvas");
    canvas.width = 1280;
    canvas.height = 720;
    canvasRef.current = canvas;
    const ctx = canvas.getContext("2d");

    relayRef.current = { running: true, sourceId };

    const captureFrame = () => {
      if (!relayRef.current.running) return;

      if (video.readyState >= 2) {
        ctx.drawImage(video, 0, 0, 1280, 720);
        canvas.toBlob(
          (blob) => {
            if (blob && relayRef.current.running && bcRef.current) {
              const reader = new FileReader();
              reader.onloadend = () => {
                try {
                  bcRef.current.postMessage({
                    type: "camera_frame",
                    dataUrl: reader.result,
                    audioEnabled: cameraStreams[sourceId]?.audioEnabled || false,
                    timestamp: Date.now(),
                  });
                } catch {}
              };
              reader.readAsDataURL(blob);
            }
          },
          "image/jpeg",
          0.75
        );
      }

      setTimeout(() => {
        if (relayRef.current.running) requestAnimationFrame(captureFrame);
      }, 50); // ~20fps
    };

    requestAnimationFrame(captureFrame);
  }, [cameraStreams, eventId]);

  // Stop BroadcastChannel relay
  const stopRelay = useCallback(() => {
    relayRef.current.running = false;
    relayRef.current.sourceId = null;
    if (bcRef.current) {
      try { bcRef.current.postMessage({ type: "camera_stop" }); } catch {}
      bcRef.current.close();
      bcRef.current = null;
    }
    if (videoRelayRef.current) {
      videoRelayRef.current.pause();
      videoRelayRef.current.srcObject = null;
      videoRelayRef.current = null;
    }
    canvasRef.current = null;
  }, []);

  // Clean up all streams on unmount
  useEffect(() => {
    return () => {
      Object.values(cameraStreams).forEach(entry => {
        if (entry?.stream) entry.stream.getTracks().forEach(t => t.stop());
      });
      stopRelay();
    };
  }, []);

  // Listen for device changes (plug/unplug)
  useEffect(() => {
    const handler = () => {
      navigator.mediaDevices.enumerateDevices().then(allDevices => {
        setDevices(allDevices.filter(d => d.kind === "videoinput"));
      }).catch(() => {});
    };
    navigator.mediaDevices?.addEventListener?.("devicechange", handler);
    return () => navigator.mediaDevices?.removeEventListener?.("devicechange", handler);
  }, []);

  return {
    cameraStreams,
    devices,
    permissionState,
    enumerateDevices,
    startCamera,
    stopCamera,
    toggleAudio,
    startRelay,
    stopRelay,
  };
}
